import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import Page from '../../components/Page';
import Loader from '../../components/Loader';
import { useAppDispatch, useAppSelector } from '../../store';
import { findAdminAnalytics } from '../../store/adminAnalytics/api';
import { TIME_PERIOD } from '../../constants';
import { TimePeriod } from '../../enum';
import { searchDateFormat } from '../../utils/searchDateFormat';
import {
  AnalyticsKpiCard,
  formatMoney,
  SectionHeader,
} from './components/AnalyticsKpiCard';
import {
  StatusDonutChart,
} from './components/AnalyticsCharts';
import { SalesFunnel } from './components/SalesFunnel';
import { TopSellingCakesSection } from './components/TopSellingCakesSection';
import { SalesAnalyticsSection } from './components/SalesAnalyticsSection';
import {
  getCustomRangeDayCount,
  getSalesViewConfig,
} from './components/salesDurationViewConfig';
import { CustomerAnalyticsSection } from './components/CustomerAnalyticsSection';
import { analyticsColors } from './components/analyticsStyles';
import { MdAttachMoney, MdShoppingCart, MdPeople, MdInventory2 } from 'react-icons/md';
import { FaGift, FaRedo, FaUserPlus } from 'react-icons/fa';
import { VscGraphLine } from 'react-icons/vsc';

const PRODUCT_TABS = [
  { key: 'topSelling', label: 'Top Selling' },
  { key: 'highestRevenue', label: 'Highest Revenue' },
  { key: 'repeatPurchase', label: 'Repeat Purchase' },
];

const AnalyticsDashboard = () => {
  const dispatch = useAppDispatch();
  const { data, isLoading } = useAppSelector((state) => state.adminAnalytics);
  const { firstName, lastName, userRole } = useAppSelector(
    (state) => state.auth.user
  );
  const [timeDuration, setTimeDuration] = useState<string>(
    TimePeriod.lastThirtyDays
  );
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [productTab, setProductTab] = useState(0);
  const [customerSort, setCustomerSort] = useState<'spending' | 'orders'>(
    'spending'
  );
  const [salesView, setSalesView] = useState<
    'daily' | 'weekly' | 'biweekly' | 'monthly'
  >('daily');

  const salesViewConfig = useMemo(() => {
    const rangeDays =
      timeDuration === TimePeriod.customRange && startDate && endDate
        ? getCustomRangeDayCount(startDate, endDate)
        : undefined;

    return getSalesViewConfig(timeDuration, rangeDays);
  }, [timeDuration, startDate, endDate]);

  const fetchData = () => {
    if (userRole !== 'admin' && userRole !== 'finance') return;

    if (timeDuration !== TimePeriod.customRange) {
      dispatch(
        findAdminAnalytics({
          timePeriod: timeDuration,
        })
      );
    } else if (startDate && endDate) {
      dispatch(
        findAdminAnalytics({
          timePeriod: timeDuration,
          startDate: searchDateFormat(startDate),
          endDate: searchDateFormat(endDate),
        })
      );
    }
  };

  useEffect(() => {
    fetchData();
  }, [dispatch, timeDuration, startDate, endDate]);

  useEffect(() => {
    setSalesView(salesViewConfig.defaultView);
  }, [timeDuration, startDate, endDate]);

  if (userRole !== 'admin' && userRole !== 'finance') {
    return <Page title='Analytics Dashboard'>{null}</Page>;
  }

  const overview = data?.overview;
  const sales = data?.salesAnalytics;
  const productKey = PRODUCT_TABS[productTab]?.key as
    | 'topSelling'
    | 'highestRevenue'
    | 'repeatPurchase';
  const productRows = data?.productAnalytics?.[productKey] || [];
  const topCustomers =
    customerSort === 'orders'
      ? data?.topCustomersByOrders ?? data?.topCustomers
      : data?.topCustomersBySpending ?? data?.topCustomers;

  return (
    <Page title='Analytics Dashboard'>
      <Box sx={{ bgcolor: analyticsColors.pageBg, minHeight: '100vh', pb: 4 }}>
        <Container maxWidth='xl'>
        {isLoading ? (
          <Box sx={{ height: '76vh', display: 'flex', alignItems: 'center' }}>
            <Loader />
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ pt: 3 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant='h4'>
                  Hi, {firstName} {lastName}. Dashboard Overview
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3} />
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Time Duration</InputLabel>
                  <Select
                    value={timeDuration}
                    label='Time Duration'
                    onChange={(e) => setTimeDuration(e.target.value)}
                  >
                    {TIME_PERIOD.map((time) => (
                      <MenuItem key={time.value} value={time.value}>
                        {time.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {timeDuration === TimePeriod.customRange && (
              <Grid container spacing={2} sx={{ py: 2 }}>
                <Grid item xs={12} sm={4} />
                <Grid item xs={12} sm={4}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label='Start Date'
                      value={startDate}
                      maxDate={endDate ?? undefined}
                      onChange={setStartDate}
                      inputFormat='dd/MM/yyyy'
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label='End Date'
                      value={endDate}
                      minDate={startDate ?? undefined}
                      onChange={setEndDate}
                      inputFormat='dd/MM/yyyy'
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            )}

            {/* Overview KPIs */}
            <Grid container spacing={2} sx={{ pt: 2 }}>
              <Grid item xs={12} sm={6} md={4}>
                <AnalyticsKpiCard
                  title='Total Sales'
                  value={formatMoney(overview?.totalSales || 0)}
                  changePercent={overview?.salesChangePercent}
                  previousLabel={`vs ${formatMoney(overview?.previousTotalSales || 0)} previous period`}
                  icon={<MdAttachMoney />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <AnalyticsKpiCard
                  title='Total Orders'
                  value={overview?.totalOrders || 0}
                  changePercent={overview?.ordersChangePercent}
                  previousLabel={`vs ${overview?.previousTotalOrders || 0} previous period`}
                  icon={<MdShoppingCart />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <AnalyticsKpiCard
                  title='Average Order Value'
                  value={formatMoney(overview?.averageOrderValue || 0)}
                  changePercent={overview?.aovChangePercent}
                  previousLabel={`vs ${formatMoney(overview?.previousAverageOrderValue || 0)} previous period`}
                  icon={<VscGraphLine />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <AnalyticsKpiCard
                  title='Number of Customers'
                  value={overview?.totalCustomers || 0}
                  subtext='unique customers this period'
                  icon={<MdPeople />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <AnalyticsKpiCard
                  title='Returning Customers'
                  value={`${overview?.returningCustomersPercent || 0}%`}
                  subtext='of total customers'
                  icon={<FaRedo />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <AnalyticsKpiCard
                  title='New Customers'
                  value={overview?.newCustomers || 0}
                  subtext='first-time buyers'
                  icon={<FaUserPlus />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <AnalyticsKpiCard
                  title='Total Items Sold'
                  value={overview?.totalItemsSold || 0}
                  subtext='cakes, toppings & add-ons combined'
                  icon={<MdInventory2 />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <AnalyticsKpiCard
                  title='Refunds / Cancelled'
                  value={overview?.cancelledOrdersCount || 0}
                  subtext={`${formatMoney(overview?.cancelledOrdersValue || 0)} total value`}
                  icon={<FaGift />}
                />
              </Grid>
            </Grid>

            {/* Top Selling Cakes */}
            <TopSellingCakesSection cakes={data?.topSellingCakes} />

            {/* Sales Analytics */}
            <SalesAnalyticsSection
              sales={sales}
              salesView={salesView}
              onSalesViewChange={setSalesView}
              availableViews={salesViewConfig.availableViews}
              timeDuration={timeDuration}
              customRangeStart={
                timeDuration === TimePeriod.customRange ? startDate : null
              }
              customRangeEnd={
                timeDuration === TimePeriod.customRange ? endDate : null
              }
              revenueTrend={data?.revenueTrend}
              ordersTrend={data?.ordersTrend}
            />

            {/* Product Analytics */}
            <Paper sx={{ p: { xs: 1.5, sm: 2 }, mt: 3, minWidth: 0, overflow: 'hidden' }}>
              <SectionHeader
                title='Product Analytics'
                action={
                  <Tabs
                    value={productTab}
                    onChange={(_, v) => setProductTab(v)}
                    variant='scrollable'
                    scrollButtons='auto'
                    allowScrollButtonsMobile
                    sx={{
                      minHeight: 40,
                      maxWidth: '100%',
                      '& .MuiTabs-flexContainer': {
                        gap: { xs: 0.5, sm: 0 },
                      },
                      '& .MuiTab-root': {
                        minHeight: 40,
                        minWidth: 'auto',
                        px: { xs: 1.25, sm: 2 },
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: { xs: 13, sm: 14 },
                        whiteSpace: 'nowrap',
                      },
                      '& .Mui-selected': {
                        color: 'primary.main',
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: 'primary.main',
                      },
                    }}
                  >
                    {PRODUCT_TABS.map((tab) => (
                      <Tab key={tab.key} label={tab.label} />
                    ))}
                  </Tabs>
                }
              />
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size='small' sx={{ minWidth: 420 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Product Name</TableCell>
                      <TableCell align='right'>Orders</TableCell>
                      <TableCell align='right'>Total Revenue</TableCell>
                      {productKey === 'repeatPurchase' && (
                        <TableCell align='right'>Repeat Rate</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productRows.map((row: any) => (
                      <TableRow key={`${row.name}-${row.rank}`}>
                        <TableCell>{row.rank}</TableCell>
                        <TableCell>{row.name}</TableCell>
                        <TableCell align='right'>{row.orders}</TableCell>
                        <TableCell align='right'>
                          {formatMoney(row.revenue)}
                        </TableCell>
                        {productKey === 'repeatPurchase' && (
                          <TableCell align='right'>{row.repeatRate}%</TableCell>
                        )}
                      </TableRow>
                    ))}
                    {!productRows.length && (
                      <TableRow>
                        <TableCell colSpan={5} align='center'>
                          No record found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <CustomerAnalyticsSection
              customerAnalytics={data?.customerAnalytics}
              clvSegments={data?.clvSegments}
            />

            <Paper elevation={0} sx={{ p: 2, mt: 3 }}>
              <SectionHeader
                title={
                  customerSort === 'orders'
                    ? 'Top Customers by Orders'
                    : 'Top Customers by Spending'
                }
                subtitle='Ranked for the selected period'
                action={
                  <ToggleButtonGroup
                    size='small'
                    value={customerSort}
                    exclusive
                    onChange={(_, v) => v && setCustomerSort(v)}
                  >
                    <ToggleButton value='spending'>Top by Spending</ToggleButton>
                    <ToggleButton value='orders'>Most Orders</ToggleButton>
                  </ToggleButtonGroup>
                }
              />
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer Name</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell align='right'>Orders</TableCell>
                      <TableCell align='right'>Spending</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topCustomers?.map((c) => (
                      <TableRow key={c.phoneNumber}>
                        <TableCell>{c.customerName}</TableCell>
                        <TableCell>{c.phoneNumber}</TableCell>
                        <TableCell align='right'>{c.ordersCount}</TableCell>
                        <TableCell align='right'>
                          {formatMoney(c.totalSpending)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!topCustomers?.length && (
                      <TableRow>
                        <TableCell colSpan={4} align='center'>
                          No record found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Operational Analytics */}
            <Typography variant='h6' sx={{ mt: 4, mb: 2 }}>
              Operational Analytics
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <AnalyticsKpiCard
                  title='Avg Preparation Time'
                  value={`${data?.operational?.avgPreparationTime || 0} min`}
                  changePercent={data?.operational?.prepTimeChangePercent}
                  previousLabel={`vs ${data?.operational?.previousAvgPreparationTime || 0} min previous period`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <AnalyticsKpiCard
                  title='On-Time Delivery'
                  value={`${data?.operational?.onTimeDelivery || 0}%`}
                  changePercent={data?.operational?.onTimeChangePercent}
                  previousLabel={`vs ${data?.operational?.previousOnTimeDelivery || 0}% previous period`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <AnalyticsKpiCard
                  title='Cancelled Orders'
                  value={data?.operational?.cancelledOrders || 0}
                  subtext={`${data?.operational?.cancelledPercent || 0}% of total orders`}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={5}>
                <StatusDonutChart breakdown={data?.operational?.statusBreakdown} />
              </Grid>
              <Grid item xs={12} md={7}>
                <SalesFunnel
                  data={[
                    {
                      label: 'Carts Created',
                      value: data?.salesFunnel?.cartsCreated ?? 0,
                    },
                    {
                      label: 'Orders Placed',
                      value: data?.salesFunnel?.ordersPlaced ?? 0,
                    },
                    {
                      label: 'Returning Customers',
                      value: data?.salesFunnel?.returningCustomers ?? 0,
                    },
                  ]}
                />
              </Grid>
            </Grid>
          </>
        )}
        </Container>
      </Box>
    </Page>
  );
};

export default AnalyticsDashboard;
